<template>
  <div class="w-100 h-100">
    <b-container fluid>
      <h2 class="tw-mb-0 text-aegis-dk">
        Summary Slide Report
      </h2>
      <p class="tw-italic tw-text-gray-700">
        View/Download curated Summary Slide report with date range filter.
      </p>
    </b-container>

    <b-container fluid class="tw-my-5">
      <b-row>
        <!-- date range filter: start date -->
        <b-col cols="3">
          <label for="startDate">Start Date</label>
          <b-form-datepicker
            id="start_date"
            :max="endDate"
            v-model="startDate"
            placeholder="Start Date"
            class="mb-2"
            reset-button
          />
        </b-col>
        <!-- date range filter: end date -->
        <b-col lg="3">
          <label for="endDate">End Date</label>
          <b-form-datepicker
            id="end_date"
            :min="startDate"
            :max="this.getToday()"
            v-model="endDate"
            placeholder="End Date"
            class="mb-2"
            reset-button
            :disabled='startDate==""'
          />
        </b-col>

        <!-- download button -->
        <b-col
          v-if="totalItems"
          cols="2"
          class="text-right"
        >
          <b-button
            size="lg"
            @click="downloadFile()"
            variant="secondary"
            target="_blank"
            class="px-5"
          >
            {{ csvLoading
                ? "Downloading..."
                : csvLoadingError
                  ? "Error"
                  : "Download"
            }}
          </b-button>
        </b-col>

      </b-row>
    </b-container>

    <b-container fluid>
      <!-- add loading/error banner -->
      <b-alert v-if="loading" variant="info" show>Loading... <i class="fas fa-spin fa-spinner"></i></b-alert>
      <b-alert v-if="loadingError" variant="danger" show>Cannot load data <i class="fas fa-spin fa-spinner"></i></b-alert>

      <b-table
        ref="table"
        striped
        hover
        responsive
        bordered
        :items="data"
        :fields="fields"
        :busy.sync="loading"
        :current-page="options.currentPage"
        :per-page="options.perPage"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
      >
        <template v-slot:cell(age_at_enrollment)="data">
            {{parseFloat(data.value).toPrecision(2)}}
        </template>
    </b-table>
    </b-container>

    <b-pagination
      v-if="totalItems"
      v-model="options.currentPage"
      :total-rows="options.totalRows"
      :per-page="options.perPage"
      align="right"
      size="sm"
      class="my-0"
    ></b-pagination>
  </div>
</template>

<script>
import { HTTP } from "@/utilities/http-common";
import HandleErrors from "@/utilities/handle-errors";

import DashboardBaseTemplate from "@/layouts/DashboardBaseTemplate.vue";

import * as _ from "lodash";


export default {
  mixins: [DashboardBaseTemplate],
  data() {
    return {
      last_update: null,
      options: {
        currentPage: 1,
        totalRows: 0,
        // set default 35 per page to show Total in a single page
        perPage: 35,
      },
      loading: false,
      csvLoading: false,
      loadingError: false,
      csvLoadingError: false,
      search: "",
      fields: [],
      data: [],
      sortBy: "id",
      sortDesc: false,
      title: "",
      totalItems: 0,
      stacked: false,
      // set default start_date as 2023-01-01, which is before F01_Target_Date
      startDate: '2023-01-01',
      // set default end_date as today and consider timezone offset with UTC
      endDate: this.getToday(),
    };
  },
  computed: {},
  watch: {
    // data request when start_date or end_date is changed
    startDate: function(){
      this.find();
    },
    endDate: function(){
      this.find();
    }
  },
  created() {
    // load initial data for full range
    this.find();
  },
  methods: {
    // table data request
    find() {
      const that = this;
      that.loading = true;
      that.dateError = false;

      let params = {
        start_date: that.startDate,
        end_date: that.endDate,
      }

      return HTTP.get("/srilanka/report/summary-slide-report", {
        params: params
      })
      .then((response) => {
        that.loading = false;
        let fields = [];
        _.each(response.data.fields, function(field) {
          fields.push({
            key: field,
            sortable: true,
          });
        });
        that.fields = fields;

        that.data = response.data.data;
        that.options.totalRows = that.data.length;
        that.totalItems = that.data.length;
      })
      .catch((error) => {
        that.loading = false;
        that.loadingError = true;
        HandleErrors.HandleErrors(error);
        // set field and data to be empty
        that.fields = [];
        that.data = [];
        that.options.totalRows = that.data.length;
        that.totalItems = that.data.length;
      });
    },
    // download CSV
    downloadFile: function() {
      const that = this;
      that.csvLoading = true;

      let params = {
        start_date: that.startDate,
        end_date: that.endDate,
        // request CSV response
        return_as: 'csv',
      }

      HTTP.get('/srilanka/report/summary-slide-report', {params: params})
      .then(response => {
        var fileURL = window.URL.createObjectURL(
            new Blob([response.data])
        );
        var fileLink = document.createElement("a");
        fileLink.href = fileURL;
        // date format: yyyy-mm-dd
        const filename = 'SL_Summary_Slide_Report-' + that.startDate.replace(/-/g, "") + '-' + that.endDate.replace(/-/g, "") + '.csv';
        fileLink.setAttribute("download", filename);
        document.body.appendChild(fileLink);
        fileLink.click();
        that.csvLoading = false;
      }).catch((error) => {
        that.csvLoading = false;
        that.csvLoadingError = true;
        HandleErrors.HandleErrors(error);
      });
    },
    // get today by considering timezone offset with UTC
    getToday: function() {
      let today = new Date();
      const offset = today.getTimezoneOffset();
      today = new Date(today.getTime() - (offset*60*1000));

      return today.toISOString().split('T')[0];
    }
  }
};
</script>

<style></style>
